/**
 * Referral Signup (sections/referral-signup.liquid)
 *
 * Submits the referral opt-in form through `RivoAPI.referrer_opt_in(email, name, accepts_marketing)`
 * and swaps the form for the resulting referral link, ready to copy or share. The section title
 * stays put; the copy beneath it is swapped for the post-referral wording at the same time.
 *
 * Once the link exists the panel offers three hand-offs:
 * - Copy — clipboard, with a textarea fallback for insecure contexts.
 * - Share on SMS / Share via email — opens the customer's own messaging app with the link
 *   prefilled (`sms:` / `mailto:`). Nothing is sent on their behalf; they pick the recipient.
 * The shared link carries the campaign from the section's `referral_campaign_id` setting (as
 * Rivo's `rci` parameter): `referrer_opt_in` itself takes only email / name / consent, so the
 * campaign is attributed when the friend claims the link, not when the advocate opts in.
 *
 * - View My Stats — a popup whose button calls `RivoAPI.send_advocate_email_stats(email)` to
 *   mail the advocate their referral stats. That call resolves the raw response rather than
 *   gating on `success`, so the flag is checked here.
 *
 * The opt-in response carries a `referral_code` but no link, e.g.
 *   { id: 9565847912606, email: "…", referral_code: "qqjm9DzlfXQDD1qdP", state: "opted_in", status: true }
 * so the shareable URL is built from the section's link template, which defaults to
 * `/?referral_code={code}` — e.g. https://bruntworkwear.com/?referral_code=MvAqGmNZtB9Ql54Q1.
 * If a future response does include a URL field, that value is preferred over the template.
 *
 * `accepts_marketing` is always true — the form is an explicit marketing opt-in, and the
 * section exposes a consent-text setting for the accompanying wording.
 */
(function referralSignup() {
  /** RivoAPI is injected by the Rivo app embed and often lands after DOMContentLoaded. */
  const RIVO_WAIT_TIMEOUT_MS = 8000;
  const RIVO_POLL_INTERVAL_MS = 150;

  /** How long the copy button shows its confirmation label before reverting. */
  const COPIED_RESET_MS = 2000;

  const GENERIC_ERROR = "Something went wrong. Please try again.";

  /**
   * @param {string} name
   * @returns {((...args: any[]) => unknown) | null}
   */
  function getRivoFn(name) {
    const fn = window.RivoAPI?.[name];
    return typeof fn === "function" ? fn.bind(window.RivoAPI) : null;
  }

  /**
   * Resolves once RivoAPI exposes `name`, or null if it never shows up.
   * @param {string} name
   * @param {number} timeoutMs
   * @returns {Promise<((...args: any[]) => unknown) | null>}
   */
  function waitForRivoFn(name, timeoutMs) {
    const immediate = getRivoFn(name);
    if (immediate) return Promise.resolve(immediate);

    return new Promise(resolve => {
      const deadline = Date.now() + timeoutMs;
      const timer = window.setInterval(() => {
        const fn = getRivoFn(name);
        if (fn) {
          window.clearInterval(timer);
          resolve(fn);
          return;
        }
        if (Date.now() > deadline) {
          window.clearInterval(timer);
          resolve(null);
        }
      }, RIVO_POLL_INTERVAL_MS);
    });
  }

  /** Keys that may already hold a finished referral link. */
  const REFERRAL_URL_KEYS = ["referral_url", "referral_link", "referrer_url", "share_url"];

  /** How deep to look for referral fields inside the response. */
  const MAX_SEARCH_DEPTH = 4;

  /**
   * Pulls referral fields out of the opt-in response.
   *
   * RivoAPI has been observed returning the payload both at the top level and wrapped
   * (e.g. under `record`), so this walks the response breadth-first rather than assuming
   * one shape. Depth-capped and cycle-guarded.
   *
   * @param {unknown} response
   * @returns {{ url: string|null, code: string|null, message: string|null }}
   */
  function findReferral(response) {
    /** @type {{ url: string|null, code: string|null, message: string|null }} */
    const found = { url: null, code: null, message: null };
    if (!response || typeof response !== "object") return found;

    const seen = new Set();
    const queue = [{ node: /** @type {Record<string, unknown>} */ (response), depth: 0 }];

    while (queue.length > 0) {
      const { node, depth } = queue.shift();
      if (!node || typeof node !== "object" || seen.has(node)) continue;
      seen.add(node);

      const str = key => {
        const value = node[key];
        return typeof value === "string" && value.trim() !== "" ? value.trim() : null;
      };

      if (!found.url) {
        for (const key of REFERRAL_URL_KEYS) {
          const value = str(key);
          if (value) {
            found.url = value;
            break;
          }
        }
      }
      if (!found.code) found.code = str("referral_code");
      if (!found.message) found.message = str("message") || str("error");

      if (depth < MAX_SEARCH_DEPTH) {
        for (const value of Object.values(node)) {
          if (value && typeof value === "object") queue.push({ node: value, depth: depth + 1 });
        }
      }
    }

    return found;
  }

  /**
   * Query parameter Rivo reads a referral campaign from (`rci` in its own page config).
   */
  const CAMPAIGN_PARAM = "rci";

  /**
   * Expands the link template. `{code}` is the referral code; `{campaign}` is optional — with a
   * campaign configured and no token in the template, `rci` is appended instead, which is how
   * Rivo attributes a claimed referral to a campaign.
   * @param {string} template - e.g. `/?referral_code={code}`
   * @param {string} code
   * @param {string} [campaignId]
   * @returns {string}
   */
  function buildReferralUrl(template, code, campaignId) {
    const encoded = encodeURIComponent(code);
    const campaign = (campaignId || "").trim();
    let filled = (template || "/?referral_code={code}").split("{code}").join(encoded);

    const hasCampaignToken = filled.includes("{campaign}");
    if (hasCampaignToken) filled = filled.split("{campaign}").join(encodeURIComponent(campaign));

    try {
      const url = new URL(filled, window.location.origin);
      if (campaign && !hasCampaignToken) {
        url.searchParams.set(CAMPAIGN_PARAM, campaign);
      }
      // A `{campaign}` token with no campaign configured would otherwise leave `rci=` behind.
      if (!campaign && url.searchParams.get(CAMPAIGN_PARAM) === "") {
        url.searchParams.delete(CAMPAIGN_PARAM);
      }
      return url.toString();
    } catch {
      return filled;
    }
  }

  /**
   * Fills the share message template. `{link}` marks where the referral URL goes; without it
   * the URL is appended, so a merchant cannot accidentally send a message with no link in it.
   * @param {string} template
   * @param {string} url
   * @returns {string}
   */
  function buildShareMessage(template, url) {
    const text = (template || "").trim();
    if (text === "") return url;
    if (text.includes("{link}")) return text.split("{link}").join(url);
    return `${text} ${url}`;
  }

  /**
   * Hand-off targets for the customer's own apps. The SMS form is `sms:?&body=` because iOS
   * wants `sms:&body=` and Android wants `sms:?body=`; the combined form satisfies both.
   * @param {{ url: string, message: string, subject: string }} opts
   * @returns {{ sms: string, email: string }}
   */
  function buildShareTargets(opts) {
    const body = encodeURIComponent(buildShareMessage(opts.message, opts.url));
    return {
      sms: `sms:?&body=${body}`,
      email: `mailto:?subject=${encodeURIComponent(opts.subject || "")}&body=${body}`,
    };
  }

  /**
   * Clipboard API needs a secure context; the textarea fallback covers the rest.
   * @param {string} text
   * @returns {Promise<boolean>}
   */
  async function copyToClipboard(text) {
    if (navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(text);
        return true;
      } catch {
        // Fall through to the legacy path below.
      }
    }

    const scratch = document.createElement("textarea");
    scratch.value = text;
    scratch.setAttribute("readonly", "");
    scratch.style.position = "fixed";
    scratch.style.top = "-9999px";
    document.body.appendChild(scratch);

    try {
      scratch.select();
      return document.execCommand("copy");
    } catch {
      return false;
    } finally {
      scratch.remove();
    }
  }

  /**
   * @param {HTMLElement} section
   */
  function initSection(section) {
    const form = section.querySelector("[data-referral-form]");
    const nameInput = section.querySelector("[data-referral-name]");
    const emailInput = section.querySelector("[data-referral-email]");
    const submit = section.querySelector("[data-referral-submit]");
    const errorEl = section.querySelector("[data-referral-error]");
    const result = section.querySelector("[data-referral-result]");
    const introCopy = section.querySelector("[data-referral-intro-copy]");
    const successCopy = section.querySelector("[data-referral-success-copy]");
    const linkInput = section.querySelector("[data-referral-link]");
    const copyBtn = section.querySelector("[data-referral-copy]");
    const copyStatus = section.querySelector("[data-referral-copy-status]");
    const shareLinks = section.querySelectorAll("[data-referral-share]");
    const statsOpen = section.querySelector("[data-referral-stats-open]");
    const statsHost = section.querySelector("[data-referral-stats-dialog]");
    const statsDialog = statsHost?.querySelector("dialog") || null;
    const statsCopyEl = section.querySelector("[data-referral-stats-copy]");
    const statsErrorEl = section.querySelector("[data-referral-stats-error]");
    const statsSuccessEl = section.querySelector("[data-referral-stats-success]");
    const statsSendBtn = section.querySelector("[data-referral-stats-send]");

    if (!(form instanceof HTMLFormElement)) return;
    if (!(nameInput instanceof HTMLInputElement) || !(emailInput instanceof HTMLInputElement)) return;

    const template = section.dataset.referralUrlTemplate || "/?referral_code={code}";
    const copyLabel = section.dataset.copyLabel || "Copy";
    const copiedLabel = section.dataset.copiedLabel || "Copied";
    const campaignId = section.dataset.referralCampaignId || "";
    const shareMessage = section.dataset.shareMessage || "";
    const shareSubject = section.dataset.shareSubject || "";
    const statsCopyTemplate = section.dataset.statsCopy || "Your stats will be sent to {email}.";
    const statsSuccessCopy = section.dataset.statsSuccess || "Sent.";

    /** Advocate address for the stats email: the submitted one, else the signed-in customer's. */
    let advocateEmail = section.dataset.advocateEmail || "";

    let submitting = false;
    let sendingStats = false;
    /** @type {number | undefined} */
    let copiedTimer;

    /** @param {string} message */
    function showError(message) {
      if (!(errorEl instanceof HTMLElement)) return;
      errorEl.textContent = message;
      errorEl.hidden = message === "";
    }

    function clearError() {
      showError("");
      nameInput.removeAttribute("aria-invalid");
      emailInput.removeAttribute("aria-invalid");
    }

    /** @param {boolean} busy */
    function setBusy(busy) {
      submitting = busy;
      if (submit instanceof HTMLButtonElement) {
        submit.disabled = busy;
        submit.setAttribute("aria-busy", busy ? "true" : "false");
      }
    }

    /**
     * Native constraint validation, surfaced in our own error region so the
     * message is announced rather than shown in a transient browser bubble.
     * @returns {boolean}
     */
    function validate() {
      if (nameInput.value.trim() === "") {
        nameInput.setAttribute("aria-invalid", "true");
        showError("Please enter your first name.");
        nameInput.focus();
        return false;
      }
      if (!emailInput.checkValidity() || emailInput.value.trim() === "") {
        emailInput.setAttribute("aria-invalid", "true");
        showError("Please enter a valid email address.");
        emailInput.focus();
        return false;
      }
      return true;
    }

    /** @param {string} url */
    function showResult(url) {
      // Only swap the form out if there is a result panel to swap in.
      if (!(result instanceof HTMLElement) || !(linkInput instanceof HTMLInputElement)) return;

      linkInput.value = url;

      // Point the share links at the customer's own apps. `sms:` / `mailto:` hand off to the OS
      // handler and leave the page in place, so the panel is still here when they come back.
      const targets = buildShareTargets({ url, message: shareMessage, subject: shareSubject });
      shareLinks.forEach(link => {
        if (!(link instanceof HTMLAnchorElement)) return;
        const target = targets[link.dataset.referralShare || ""];
        if (target) link.href = target;
      });

      // The design keeps the same title across both states and only rewords the line under it.
      if (successCopy instanceof HTMLElement) {
        successCopy.hidden = false;
        if (introCopy instanceof HTMLElement) introCopy.hidden = true;
      }

      form.hidden = true;
      result.hidden = false;

      // Move focus to the link so keyboard and screen-reader users land on the new content.
      linkInput.focus();
      linkInput.select();
    }

    form.addEventListener("submit", async event => {
      event.preventDefault();
      if (submitting) return;

      clearError();
      if (!validate()) return;

      setBusy(true);

      try {
        const optIn = await waitForRivoFn("referrer_opt_in", RIVO_WAIT_TIMEOUT_MS);
        if (!optIn) {
          showError("Referrals are unavailable right now. Please try again shortly.");
          return;
        }

        const submittedEmail = emailInput.value.trim();
        const response = await Promise.resolve(optIn(submittedEmail, nameInput.value.trim(), true));
        // The address Rivo just opted in is the advocate, whoever is signed in.
        if (submittedEmail) advocateEmail = submittedEmail;

        const found = findReferral(response);
        // Rivo's own opt-in response URL carries no campaign, so a configured campaign means the
        // link is rebuilt from the code rather than used as returned.
        const useReturnedUrl = found.url && !campaignId;
        const referralUrl = useReturnedUrl
          ? found.url
          : found.code
            ? buildReferralUrl(template, found.code, campaignId)
            : found.url || null;

        // A link is the only success signal that matters: a response carrying a code is a
        // success even when it also reports `status: false` (e.g. "already opted in").
        if (referralUrl) {
          showResult(referralUrl);
          return;
        }

        // Log the payload so an unexpected response shape stays diagnosable from the console.
        console.warn("[referral-signup] no referral code in referrer_opt_in response", response);
        showError(found.message || GENERIC_ERROR);
      } catch (error) {
        console.error("[referral-signup] referrer_opt_in failed", error);
        showError(GENERIC_ERROR);
      } finally {
        setBusy(false);
      }
    });

    if (copyBtn instanceof HTMLElement) {
      copyBtn.addEventListener("click", async () => {
        if (!(linkInput instanceof HTMLInputElement) || linkInput.value === "") return;

        const ok = await copyToClipboard(linkInput.value);
        const label = copyBtn.querySelector(".btn__label") || copyBtn;

        if (ok) {
          label.textContent = copiedLabel;
          if (copyStatus instanceof HTMLElement) copyStatus.textContent = `Referral link copied: ${linkInput.value}`;
          window.clearTimeout(copiedTimer);
          copiedTimer = window.setTimeout(() => {
            label.textContent = copyLabel;
          }, COPIED_RESET_MS);
        } else {
          // Copying was blocked; leave the link selected so the customer can copy manually.
          linkInput.focus();
          linkInput.select();
          if (copyStatus instanceof HTMLElement) {
            copyStatus.textContent = "Couldn't copy automatically. The link is selected — copy it manually.";
          }
        }
      });
    }

    /** @param {string} message */
    function showStatsError(message) {
      if (!(statsErrorEl instanceof HTMLElement)) return;
      statsErrorEl.textContent = message;
      statsErrorEl.hidden = message === "";
    }

    /** @param {boolean} busy */
    function setStatsBusy(busy) {
      sendingStats = busy;
      if (statsSendBtn instanceof HTMLButtonElement) {
        statsSendBtn.disabled = busy;
        statsSendBtn.setAttribute("aria-busy", busy ? "true" : "false");
      }
    }

    if (statsOpen instanceof HTMLElement) {
      statsOpen.addEventListener("click", () => {
        if (!(statsDialog instanceof HTMLElement)) return;

        // The copy names the address the stats will go to, so the customer can see it is theirs.
        if (statsCopyEl instanceof HTMLElement) {
          statsCopyEl.textContent = statsCopyTemplate.split("{email}").join(advocateEmail);
        }
        showStatsError("");
        if (statsSuccessEl instanceof HTMLElement) {
          statsSuccessEl.textContent = "";
          statsSuccessEl.hidden = true;
        }
        if (statsSendBtn instanceof HTMLElement) statsSendBtn.hidden = false;
        setStatsBusy(false);

        if (typeof statsHost?.showDialog === "function") {
          statsHost.showDialog();
        } else if (typeof statsDialog.showModal === "function") {
          // dialog.js is loaded `fetchpriority="low"`, so the element may not be upgraded yet.
          statsDialog.showModal();
        }
      });
    }

    if (statsSendBtn instanceof HTMLElement) {
      statsSendBtn.addEventListener("click", async () => {
        if (sendingStats) return;

        if (!advocateEmail) {
          showStatsError("We need your email address before we can send your stats.");
          return;
        }

        showStatsError("");
        setStatsBusy(true);

        try {
          const sendStats = await waitForRivoFn("send_advocate_email_stats", RIVO_WAIT_TIMEOUT_MS);
          if (!sendStats) {
            showStatsError("Stats are unavailable right now. Please try again shortly.");
            return;
          }

          // Unlike the grant calls, this one resolves the raw response, so `success` is checked
          // here rather than inferred from the resolution.
          const response = await Promise.resolve(sendStats(advocateEmail));
          if (response && typeof response === "object" && response.success === false) {
            console.warn("[referral-signup] send_advocate_email_stats reported failure", response);
            showStatsError(GENERIC_ERROR);
            return;
          }

          if (statsSuccessEl instanceof HTMLElement) {
            statsSuccessEl.textContent = statsSuccessCopy;
            statsSuccessEl.hidden = false;
          }
          statsSendBtn.hidden = true;
        } catch (error) {
          console.error("[referral-signup] send_advocate_email_stats failed", error);
          showStatsError(GENERIC_ERROR);
        } finally {
          setStatsBusy(false);
        }
      });
    }

    [nameInput, emailInput].forEach(input => input.addEventListener("input", clearError));
  }

  function init() {
    document.querySelectorAll("[data-referral-signup]").forEach(section => {
      if (!(section instanceof HTMLElement) || section.dataset.referralReady === "true") return;
      section.dataset.referralReady = "true";
      initSection(section);
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init, { once: true });
  } else {
    init();
  }

  // Theme editor re-renders the section without a full page load.
  document.addEventListener("shopify:section:load", init);
})();
